/**
 * The Agenda — collect the decisions the room needs to make, before the room.
 *
 * The offsite itself is a canonical session; the agenda is its item list. The
 * default method is impact/effort because an offsite's real question is usually
 * "what's worth the room's time", and that reads fastest as a quadrant.
 */

import { useEffect, useMemo, useState } from 'react';
import { uniqueLines } from '@/lib/uniqueLines';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Plus } from 'lucide-react';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { createSessionWithFeedback } from '@/lib/sessions/createSessionFlow';
import { SESSION_CREATE_BALL, SESSION_CREATE_LINE, useCreateRights } from '@/lib/authorization/createRights';
import { useToast } from '@/components/ui/Toast';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton, StatusChip } from '../components/primitives';

/** The offsite's default method — one the room can read off a wall. */
export const OFFSITE_TOOL_ID = 'impact-effort-matrix';

export default function AgendaScreen() {
  const app = getAppDefinition('offsite')!;
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const mayCreateSession = useCreateRights().session;
  const { showToast } = useToast();
  const sessions = useSessionStore((s) => s.sessions);
  const fetchSessions = useSessionStore((s) => s.fetchFromSupabase);

  const [name, setName] = useState('');
  const [topicsText, setTopicsText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchSessions();
  }, [fetchSessions]);

  const topics = useMemo(
    () => uniqueLines(topicsText.split('\n')),
    [topicsText],
  );

  const mine = useMemo(
    () =>
      sessions
        .filter((s) => s.facilitatorId === userId && s.selectedToolId === OFFSITE_TOOL_ID)
        .sort(
          (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
        ),
    [sessions, userId],
  );

  const canCreate = name.trim().length > 0 && topics.length >= 2 && !busy;

  const create = async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    const result = await createSessionWithFeedback({
      name: name.trim(),
      facilitatorId: userId,
      createdBy: userId,
      intent: 'rank_priorities',
      sessionContextMode: 'isolated',
      isolatedItems: topics,
      selectedToolId: OFFSITE_TOOL_ID,
      // Live, not async: an offsite happens in a room, together.
      modeConfig: { timingMode: 'sync' },
      workspaceId: getCurrentWorkspaceId() ?? undefined,
    }, { toast: showToast });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'That offsite could not be created. Try again.');
      return;
    }
    navigate(`/apps/offsite/${result.sessionId}`);
  };

  return (
    <AppShell app={app}>
      <ScreenHeading
        title="Plan the offsite"
        subtitle="Collect the decisions the room needs to make. Then run it, and leave with a record."
      />
      <BallLine ballWith={mayCreateSession ? 'you — gather the topics before the day' : SESSION_CREATE_BALL} />

      <div className="space-y-4">
        {/* Creating inserts into prioritization_sessions, whose insert rule
            (migration 754) takes SESSION_RUN: the form is drawn only for its
            holders, the line for everyone else (build contract §2.8). */}
        {mayCreateSession ? (
          <>
            <AppCard>
              <Eyebrow>What&apos;s the offsite?</Eyebrow>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Q3 leadership offsite"
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              />
            </AppCard>

            <AppCard>
              <Eyebrow>What needs deciding?</Eyebrow>
              <textarea
                value={topicsText}
                onChange={(e) => setTopicsText(e.target.value)}
                rows={7}
                placeholder={'One topic per line, e.g.\nDo we open the EU region this year?\nWhich two bets get headcount?\nWhat do we stop doing?'}
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              />
              <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                {topics.length} topic{topics.length === 1 ? '' : 's'} — collect them beforehand so the
                room spends its time deciding, not agenda-building.
              </p>
            </AppCard>

            {error ? (
              <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
                {error}
              </p>
            ) : null}

            <div className="flex justify-end">
              <PrimaryButton onClick={() => void create()} disabled={!canCreate}>
                <Plus className="h-4 w-4" /> {busy ? 'Creating…' : 'Create the offsite'}
              </PrimaryButton>
            </div>
          </>
        ) : (
          <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]" data-testid="session-create-refused">
            {SESSION_CREATE_LINE}
          </p>
        )}

        {mine.length > 0 ? (
          <AppCard className="!p-0">
            <div className="px-5 pt-5">
              <Eyebrow>Your offsites</Eyebrow>
            </div>
            <ul className="mt-2">
              {mine.slice(0, 8).map((s) => (
                <li key={s.id} className="border-b border-gray-100 last:border-0 dark:border-[var(--rule)]">
                  <button
                    type="button"
                    onClick={() => navigate(`/apps/offsite/${s.id}`)}
                    className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)]"
                  >
                    <CalendarDays className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                      {s.name}
                    </span>
                    <StatusChip
                      label={
                        s.state === 'PUBLISHED'
                          ? 'Recorded'
                          : s.state === 'RUN_COMPLETE'
                            ? 'Decided'
                            : s.state === 'CRITERIA_FINALIZED'
                              ? 'In the room'
                              : 'Planned'
                      }
                      tone={s.state === 'DRAFT' ? 'neutral' : 'green'}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </AppCard>
        ) : (
          <EmptyState
            title="No offsites yet"
            hint="Add the topics above and you'll have an agenda the room can actually decide against."
          />
        )}
      </div>
    </AppShell>
  );
}
